import * as React from 'react';

import {
  chatFetch,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';
import {
  MAX_AUDIO_BYTES,
  MAX_RECORDING_MS,
  TRANSCRIBE_URL,
  blobToBase64,
  isModelRejectedBody,
  isRetryableTranscribeStatus,
  mediaTypeEssence,
  pickRecorderMimeType,
  transcriptFromBody,
} from '@modules/_shared/components/chat/transcribeDictation';

export type DictationError =
  | { kind: 'permission' }
  | { kind: 'too-large'; bytes: number }
  | { kind: 'read-failed' }
  | { kind: 'not-json' }
  | { kind: 'no-text' }
  | { kind: 'failed'; status: number }
  | { kind: 'message'; text: string };

const isDictationSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function' &&
  typeof MediaRecorder !== 'undefined';

type Session = {
  recorder: MediaRecorder;
  stream: MediaStream;
  timer: ReturnType<typeof setTimeout>;
  discard: boolean;
  chunks: Blob[];
};

export const useDictation = ({
  modelId,
  onTranscript,
  onModelRejected,
}: {
  modelId: string | null;
  onTranscript: (text: string) => void;
  onModelRejected?: () => void;
}) => {
  const [isListening, setIsListening] = React.useState(false);
  const [isTranscribing, setIsTranscribing] = React.useState(false);
  const [error, setError] = React.useState<DictationError | null>(null);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [startedAt, setStartedAt] = React.useState<number | null>(null);
  const sessionRef = React.useRef<Session | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  // Bumped by stop() and by each start(), so a getUserMedia that resolves
  // after unmount or send does not open the mic.
  const startGenerationRef = React.useRef(0);
  const onTranscriptRef = React.useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const modelIdRef = React.useRef(modelId);
  modelIdRef.current = modelId;
  const onModelRejectedRef = React.useRef(onModelRejected);
  onModelRejectedRef.current = onModelRejected;
  const finishRef = React.useRef<() => void>(() => {});

  const isSupported = React.useMemo(() => isDictationSupported(), []);

  const releaseSession = React.useCallback((session: Session) => {
    clearTimeout(session.timer);
    for (const track of session.stream.getTracks()) {
      track.stop();
    }
    if (sessionRef.current === session) {
      sessionRef.current = null;
      setStream(null);
      setStartedAt(null);
    }
  }, []);

  const upload = React.useCallback(async (blob: Blob, mimeType: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (blob.size > MAX_AUDIO_BYTES) {
        setError({ kind: 'too-large', bytes: blob.size });
        return;
      }
      let audio: string;
      try {
        audio = await blobToBase64(blob);
      } catch {
        if (!controller.signal.aborted) {
          setError({ kind: 'read-failed' });
        }
        return;
      }
      const model = modelIdRef.current;
      const post = (modelId: string | null) =>
        chatFetch(TRANSCRIBE_URL, {
          method: 'POST',
          headers: {
            ...getChatHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            audio,
            mediaType: mediaTypeEssence(mimeType),
            ...(modelId == null ? {} : { model: modelId }),
          }),
          signal: controller.signal,
        });

      const readPayload = async (response: Response): Promise<unknown> => {
        const raw = await response.text();
        if (raw.length === 0) {
          return { error: raw };
        }
        try {
          return JSON.parse(raw) as unknown;
        } catch {
          return { error: raw };
        }
      };

      let response = await post(model);
      if (
        isRetryableTranscribeStatus(response.status) &&
        !controller.signal.aborted
      ) {
        response = await post(model);
      }
      let payload = await readPayload(response);
      // Only a rejected model id is worth a second upload: drop it and let
      // the server apply its default.
      if (
        response.status === 400 &&
        model != null &&
        isModelRejectedBody(payload) &&
        !controller.signal.aborted
      ) {
        onModelRejectedRef.current?.();
        response = await post(null);
        payload = await readPayload(response);
      }
      const parsed = transcriptFromBody(payload);
      if (controller.signal.aborted) {
        return;
      }
      if (!response.ok) {
        setError(
          'error' in parsed
            ? { kind: 'message', text: parsed.error }
            : { kind: 'failed', status: response.status },
        );
        return;
      }
      if ('code' in parsed) {
        setError({ kind: parsed.code });
        return;
      }
      if ('error' in parsed) {
        setError({ kind: 'message', text: parsed.error });
        return;
      }
      if (parsed.text.trim().length > 0) {
        onTranscriptRef.current(parsed.text);
      }
    } catch (err) {
      if (controller.signal.aborted) {
        return;
      }
      setError({
        kind: 'message',
        text: err instanceof Error ? err.message : String(err),
      });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      if (!controller.signal.aborted) {
        setIsTranscribing(false);
      }
    }
  }, []);

  const finish = React.useCallback(() => {
    const session = sessionRef.current;
    if (!session || session.recorder.state === 'inactive') {
      return;
    }
    setIsListening(false);
    setIsTranscribing(true);
    setError(null);
    session.recorder.stop();
  }, []);
  finishRef.current = finish;

  const stop = React.useCallback(() => {
    startGenerationRef.current += 1;
    const session = sessionRef.current;
    if (session) {
      session.discard = true;
      if (session.recorder.state !== 'inactive') {
        session.recorder.stop();
      } else {
        releaseSession(session);
      }
    }
    abortRef.current?.abort();
    setIsListening(false);
    setIsTranscribing(false);
  }, [releaseSession]);

  const start = React.useCallback(async () => {
    if (!isDictationSupported() || sessionRef.current) {
      return;
    }
    startGenerationRef.current += 1;
    const generation = startGenerationRef.current;
    setError(null);
    const mimeType = pickRecorderMimeType(type =>
      MediaRecorder.isTypeSupported(type),
    );
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (generation !== startGenerationRef.current) {
        return;
      }
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError({ kind: 'permission' });
        return;
      }
      setError({
        kind: 'message',
        text: err instanceof Error ? err.message : String(err),
      });
      return;
    }
    if (generation !== startGenerationRef.current || sessionRef.current) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      return;
    }
    let recorder: MediaRecorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch (err) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      if (generation === startGenerationRef.current) {
        setError({
          kind: 'message',
          text: err instanceof Error ? err.message : String(err),
        });
      }
      return;
    }
    const session: Session = {
      recorder,
      stream,
      timer: setTimeout(() => finishRef.current(), MAX_RECORDING_MS),
      discard: false,
      chunks: [],
    };
    recorder.ondataavailable = event => {
      if (event.data.size > 0) {
        session.chunks.push(event.data);
      }
    };
    recorder.onstop = () => {
      const recordedType = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(session.chunks, { type: recordedType });
      const discard = session.discard;
      releaseSession(session);
      if (discard) {
        setIsTranscribing(false);
        return;
      }
      void upload(blob, recordedType);
    };
    sessionRef.current = session;
    try {
      recorder.start();
    } catch (err) {
      releaseSession(session);
      if (generation === startGenerationRef.current) {
        setError({
          kind: 'message',
          text: err instanceof Error ? err.message : String(err),
        });
      }
      return;
    }
    setIsListening(true);
    setStream(stream);
    setStartedAt(Date.now());
  }, [releaseSession, upload]);

  const toggle = React.useCallback(() => {
    if (isListening) {
      finish();
    } else if (!isTranscribing) {
      void start();
    }
  }, [finish, isListening, isTranscribing, start]);

  React.useEffect(() => stop, [stop]);

  return {
    isSupported,
    isListening,
    isTranscribing,
    error,
    stream,
    startedAt,
    start,
    finish,
    cancel: stop,
    toggle,
    stop,
  };
};
